import {
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CardModule } from 'primeng/card';
import { ChatbotService } from './chatbot.service';
import {
  Article,
  ArticleCollection,
  ChatbotPage,
  FAQCollection,
  FAQPage,
  Conversation,
  MessageSender,
} from './chatbot.model';
import { CommonModule } from '@angular/common';
import { Models } from 'appwrite';
import { AuthService } from '@shared/services/auth.service';
import { faqCollections } from '@shared/dummyData/faq.data';
import { Subscription } from 'rxjs';
import { TextareaModule } from 'primeng/textarea';
import { FormsModule } from '@angular/forms';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-chatbot',
  imports: [
    CommonModule,
    CardModule,
    AngularSvgIconModule,
    CardModule,
    TextareaModule,
    FormsModule,
    LoadingSpinnerComponent,
  ],
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.css'],
})
export class ChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('scrollContainer') private scrollContainer!: ElementRef;

  user: Models.User<Models.Preferences> | null = null;
  openPage: ChatbotPage = ChatbotPage.HOME;
  faqCollections: FAQCollection[] = faqCollections;
  faqOpenPage: FAQPage = FAQPage.COLLECTIONS;
  faqPage2LoadedCollection: FAQCollection | null = null;
  faqPage3LoadedArticleCollection: ArticleCollection | null = null;
  faqPage4LoadedArticle: Article | null = null;
  ChatbotPage = ChatbotPage;
  FAQPage = FAQPage;
  MessageSender = MessageSender;
  faqPagesHistory: FAQPage[] = [];
  conversations: Conversation[] = [];
  messagePagesHistory: ChatbotPage[] = [];
  message: string = '';
  currentConversation: Conversation | null = null;

  private conversationsSub = Subscription.EMPTY;
  private currentConversationSub = Subscription.EMPTY;
  private userSub = Subscription.EMPTY;

  readonly _chatbotService = inject(ChatbotService);
  private readonly _authService = inject(AuthService);

  ngOnInit() {
    this.userSub = this._authService.user$.subscribe((user) => {
      this.user = user;
    });

    this.conversationsSub = this._chatbotService.conversations$.subscribe(
      (conversation) => {
        this.conversations = conversation;
      }
    );

    this.currentConversationSub =
      this._chatbotService.currentConversation$.subscribe((conversation) => {
        this.currentConversation = conversation;

        setTimeout(() => this.scrollToBottom(), 100); // allow DOM to update first
      });

    this._chatbotService.initializeConversations(); // Initialize conversations
  }

  onChatbotClose(): void {
    this._chatbotService.hideChatbot();
  }

  onPageChange(page: ChatbotPage): void {
    this.openPage = page;

    if (page === ChatbotPage.FAQ) {
      this.faqPagesHistory = [];
      this.faqOpenPage = FAQPage.COLLECTIONS;
    }

    if (page === ChatbotPage.CHAT) {
      this.messagePagesHistory = [ChatbotPage.HOME];
      this._chatbotService.startNewConversation();
      this.message = '';
    }

    if (page === ChatbotPage.MESSAGES_LIST) {
      this.messagePagesHistory = [ChatbotPage.HOME];
      this._chatbotService.getConversations();
    }
  }

  onFAQPageChange(page: FAQPage): void {
    this.faqOpenPage = page;
  }

  onConversationClick(item: Conversation): void {
    this.currentConversation = item;

    this.openPage = ChatbotPage.CHAT;
    this.messagePagesHistory.push(ChatbotPage.MESSAGES_LIST); // Set the previous page to the current page
  }

  onFAQCollectionClick(item: FAQCollection): void {
    this.faqPage2LoadedCollection = item;
    this.faqOpenPage = FAQPage.GROUPS_OF_ARTICLES_LISTS;

    // Set the previous page to the current page
    this.faqPagesHistory.push(FAQPage.COLLECTIONS);
  }

  onFAQArticleCollectionClick(item: ArticleCollection): void {
    this.faqPage3LoadedArticleCollection = item;
    this.faqOpenPage = FAQPage.ARTICLES_LIST;

    // Set the previous page to the current page
    this.faqPagesHistory.push(FAQPage.GROUPS_OF_ARTICLES_LISTS);
  }

  onFAQArticleClick(item: Article): void {
    this.faqPage4LoadedArticle = item;
    this.faqOpenPage = FAQPage.READ_ARTICLE;

    // Set the previous page to the current page
    this.faqPagesHistory.push(FAQPage.ARTICLES_LIST);
  }

  onFAQPreviousPageClick(): void {
    if (this.faqPagesHistory.length === 0) {
      return;
    }

    this.faqOpenPage = this.faqPagesHistory.pop()!; // Get the previous page
  }

  onMessagePreviousPageClick(): void {
    if (this.messagePagesHistory.length === 0) {
      return;
    }

    this.openPage = this.messagePagesHistory.pop()!; // Get the previous page

    this._chatbotService.clearConversation();
  }

  sendMessage(event: Event): void {
    event.preventDefault(); // Prevents new line
    event.stopPropagation(); // Stops bubbling

    if (!this.message.trim()) {
      return;
    }

    this._chatbotService.sendMessage(
      this.currentConversation!.id,
      this.message.trim()
    );

    this.message = ''; // Clear input
  }

  private scrollToBottom(): void {
    if (!this.scrollContainer) {
      return;
    }

    this.scrollContainer.nativeElement.scrollTop =
      this.scrollContainer.nativeElement.scrollHeight;
  }

  ngOnDestroy(): void {
    this.userSub.unsubscribe();
    this.currentConversationSub.unsubscribe();
    this.conversationsSub.unsubscribe();
    this._chatbotService.hideChatbot();
    this._chatbotService.clearConversation();
  }
}
